import { useState, useEffect, useMemo } from 'react';
import {
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    ListItemText,
    Chip,
    IconButton,
    Tooltip,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    Switch,
    Paper,
    Collapse,
} from '@mui/material';
import {
    Event as EventIcon,
    CalendarToday as CalendarIcon,
    RestartAlt as ResetIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { themeConstants } from '@/theme';
import BaseModal from '@/components/modals/BaseModal';
import {
    useCreatePlanEventMutation,
    useUpdatePlanEventMutation,
    useDeletePlanEventMutation,
    useGetEventQuery,
    useGetEventDistinctValuesQuery,
    useGetUserInfoQuery,
} from '@/store/apiSlice.ts';
import { openEventModal } from '@/store/uiSlice.ts';
import { useAppSelector, useAppDispatch, useCategoriesResources } from '@/store/hooks.ts';
import { useI18nContext } from '@/i18n/i18n-react';
import CategoryIcon from '@/components/CategoryIcon';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';

const EventModal = () => {
    const editEventData = useAppSelector((state) => state.ui.editEventData);
    const isEdit = Boolean(editEventData?.date);

    const [date, setDate] = useState('');
    const [originalDate, setOriginalDate] = useState<string>('');
    const [event, setEvent] = useState('');
    const [location, setLocation] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [excludedCategories, setExcludedCategories] = useState<number[]>([]);
    const [eventId, setEventId] = useState<number | undefined>(undefined);
    const [templateExcludedCategories, setTemplateExcludedCategories] = useState<number[]>([]);
    const [enableDateEditing, setEnableDateEditing] = useState(false);
    const [canceled, setCanceled] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(true);
    const [detailsManuallyToggled, setDetailsManuallyToggled] = useState(false);

    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.event);
    const onClose = () => dispatch(openEventModal());
    const { alert, confirm } = useAppDialog();
    const [createPlanEvent, { isLoading: isCreating }] = useCreatePlanEventMutation();
    const [updatePlanEvent, { isLoading: isUpdating }] = useUpdatePlanEventMutation();
    const [deletePlanEvent, { isLoading: isDeleting }] = useDeletePlanEventMutation();
    const categories = useCategoriesResources();

    const { data: eventTemplate } = useGetEventQuery(eventId!, {
        skip: !eventId,
    });

    const { data: distinctValues } = useGetEventDistinctValuesQuery();
    const { data: userInfo } = useGetUserInfoQuery();
    const { LL } = useI18nContext();

    const nameOptions = useMemo<string[]>(() => {
        const options = (distinctValues?.names ?? []).filter(Boolean) as string[];
        return Array.from(new Set<string>(options)).sort((a, b) => a.localeCompare(b));
    }, [distinctValues]);

    const locationOptions = useMemo<string[]>(() => {
        const options = (distinctValues?.locations ?? []).filter(Boolean) as string[];
        return Array.from(new Set<string>(options)).sort((a, b) => a.localeCompare(b));
    }, [distinctValues]);

    const isLoading = isCreating || isUpdating || isDeleting;
    const canEdit = !!(userInfo?.admin || userInfo?.eventEditor);
    const canChangeDate = !!userInfo?.admin;

    useEffect(() => {
        if (open) {
            const initialDate = editEventData.date ?? '';
            setDate(initialDate);
            setOriginalDate(initialDate);
            setEnableDateEditing(false);
            setEvent(editEventData.event ?? '');
            setLocation(editEventData.location ?? '');
            setStartTime(editEventData.startTime ?? '');
            setEndTime(editEventData.endTime ?? '');
            setExcludedCategories(editEventData.excludedCategories ?? []);
            setEventId(editEventData.eventId);
            const initialCanceled = !!editEventData.canceled;
            setCanceled(initialCanceled);

            setDetailsManuallyToggled(false);
            setDetailsExpanded(!initialCanceled);
        }
    }, [open, editEventData]);

    // Auto-collapse/expand when canceled changes (but don't fight manual user choice)
    useEffect(() => {
        if (!open) return;
        if (detailsManuallyToggled) return;
        setDetailsExpanded(!canceled);
    }, [canceled, detailsManuallyToggled, open]);

    // Update template excluded categories when template data is loaded
    useEffect(() => {
        if (eventTemplate?.excludedCategories) {
            setTemplateExcludedCategories(eventTemplate.excludedCategories);
        }
    }, [eventTemplate]);

    const handleSubmit = async () => {
        if (!canEdit) {
            await alert(LL.errors.noPermissionEditEvent());
            return;
        }
        if (!date) {
            await alert(LL.eventModal.selectDatePrompt());
            return;
        }

        // Prevent creating / moving events to a date in the past.
        const today = new Date().toISOString().slice(0, 10);
        if (date < today) {
            await alert(LL.eventModal.dateCannotBeInPast());
            return;
        }

        const isDateChanged = isEdit && originalDate && date !== originalDate;
        if (isDateChanged && !canChangeDate) {
            await alert(LL.errors.noPermissionEditEvent());
            return;
        }

        if (isDateChanged) {
            const ok = await confirm(
                LL.eventModal.changeDateConfirmMessage({ from: originalDate, to: date }),
                {
                    title: LL.eventModal.changeDateConfirmTitle(),
                    confirmText: LL.eventModal.changeDateConfirmYes(),
                    cancelText: LL.common.cancel(),
                }
            );
            if (!ok) return;
        }

        try {
            const payload = {
                date,
                event,
                location,
                startTime,
                endTime,
                excludedCategories,
                canceled,
            };

            if (!isEdit) {
                await createPlanEvent(payload).unwrap();
            } else if (isDateChanged) {
                // There is no dedicated "rename/move date" endpoint; implement as delete old + create new.
                await createPlanEvent(payload).unwrap();
                await deletePlanEvent({ date: originalDate }).unwrap();

                // If the backend sets up additional data (like eventId/template) on update,
                // try to apply it afterwards when we have it.
                if (eventId) {
                    await updatePlanEvent({ ...payload, eventId }).unwrap();
                }
            } else {
                await updatePlanEvent(payload).unwrap();
            }
        } catch (error: unknown) {
            await alert(getApiErrorMessage(error));
            return;
        }
        onClose();
    };

    const handleDelete = async () => {
        if (!canEdit) {
            await alert(LL.errors.noPermissionEditEvent());
            return;
        }
        if (!date) {
            return;
        }

        try {
            await deletePlanEvent({ date }).unwrap();
            onClose();
        } catch (error: unknown) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleResetExcludedCategories = () => {
        setExcludedCategories(templateExcludedCategories);
    };

    const handleClearExcludedCategories = () => {
        setExcludedCategories([]);
    };

    const handleResetToTemplate = async () => {
        if (!eventId || !eventTemplate) {
            return;
        }

        // Keep the chosen date; reset the editable fields to template defaults.
        setEvent(eventTemplate.name ?? '');
        setLocation(eventTemplate.location ?? '');
        setStartTime(eventTemplate.startTime ?? '');
        setEndTime(eventTemplate.endTime ?? '');
        setExcludedCategories(eventTemplate.excludedCategories ?? []);
    };

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            maxWidth="sm"
            styledHeader={{
                icon: <EventIcon sx={{ fontSize: 32, color: 'white' }} />,
                title: isEdit ? LL.eventModal.editTitle() : LL.eventModal.createTitle(),
                subtitle: [isEdit ? LL.eventModal.editSubtitle() : LL.eventModal.createSubtitle()],
                gradient: canceled
                    ? 'linear-gradient(135deg, #7a1f1f 0%, #bc4a4a 100%)'
                    : themeConstants.gradients.error,
            }}
            actions={
                canEdit ? (
                    <>
                        {eventId && eventTemplate && (
                            <Button
                                onClick={handleResetToTemplate}
                                variant="outlined"
                                color="inherit"
                                size="large"
                                disabled={isLoading}
                                startIcon={<ResetIcon />}
                                sx={{ minWidth: 160, ml: 1 }}
                            >
                                {LL.eventModal.resetToTemplate()}
                            </Button>
                        )}

                        {isEdit && (
                            <Button
                                onClick={handleDelete}
                                variant="outlined"
                                color="error"
                                size="large"
                                disabled={isLoading}
                                sx={{ minWidth: 120, ml: 1 }}
                            >
                                {LL.common.delete()}
                            </Button>
                        )}

                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            size="large"
                            color="error"
                            disabled={isLoading}
                            startIcon={
                                isLoading ? <CircularProgress size={20} color="inherit" /> : null
                            }
                            sx={{ minWidth: 120 }}
                        >
                            {isEdit ? LL.common.update() : LL.common.create()}
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose} variant="outlined" color="inherit" size="large">
                        {LL.common.close()}
                    </Button>
                )
            }
        >
            <Box sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
                <Stack sx={{ gap: 3 }}>
                    {/* Details header + expand toggle */}
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={(theme) => ({
                                px: 2,
                                py: 1.5,
                                backgroundColor: canceled
                                    ? theme.palette.warning.light
                                    : theme.palette.action.hover,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            })}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 800,
                                    }}
                                >
                                    {LL.event.event()}
                                </Typography>
                                {canceled && <Typography variant="body2">{event}</Typography>}
                            </Box>

                            <IconButton
                                size="small"
                                onClick={() => {
                                    setDetailsManuallyToggled(true);
                                    setDetailsExpanded((v) => !v);
                                }}
                                aria-label={detailsExpanded ? 'Collapse' : 'Expand'}
                            >
                                {detailsExpanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Box>

                        <Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                                <Stack sx={{ gap: 3 }}>
                                    <Box>
                                        <Stack
                                            direction="row"
                                            sx={{ alignItems: 'center', gap: 1, mb: 1 }}
                                        >
                                            <CalendarIcon
                                                sx={{ color: 'primary.main', fontSize: 20 }}
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {LL.eventModal.eventDate()}
                                            </Typography>
                                        </Stack>

                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            sx={{ gap: 1.5, alignItems: 'start' }}
                                        >
                                            <TextField
                                                fullWidth
                                                type="date"
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                disabled={isEdit ? !enableDateEditing : false}
                                                helperText={
                                                    isEdit
                                                        ? enableDateEditing
                                                            ? undefined
                                                            : LL.eventModal.dateChangeAdminOnlyHelper()
                                                        : undefined
                                                }
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        fontSize: '1.1rem',
                                                    },
                                                }}
                                            />

                                            {isEdit && canChangeDate && (
                                                <FormControlLabel
                                                    sx={{ mt: '5px', userSelect: 'none' }}
                                                    control={
                                                        <Checkbox
                                                            checked={enableDateEditing}
                                                            onChange={(_, checked) =>
                                                                setEnableDateEditing(checked)
                                                            }
                                                        />
                                                    }
                                                    label={LL.eventModal.enableDateEditing()}
                                                />
                                            )}
                                        </Stack>
                                    </Box>

                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label={LL.eventModal.startTime()}
                                            value={startTime}
                                            onChange={(e) => setStartTime(e.target.value)}
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label={LL.eventModal.endTime()}
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            }}
                                        />
                                    </Stack>

                                    <Box>
                                        <Stack
                                            direction="row"
                                            sx={{ alignItems: 'center', gap: 1, mb: 1 }}
                                        >
                                            <EventIcon sx={{ color: 'error.main', fontSize: 20 }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {LL.eventModal.eventName()}
                                            </Typography>
                                        </Stack>
                                        <Autocomplete<string, false, false, true>
                                            freeSolo
                                            options={nameOptions}
                                            value={event}
                                            onChange={(_e, value) => setEvent(value ?? '')}
                                            onInputChange={(_e, value) => setEvent(value ?? '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    placeholder={LL.eventModal.eventNamePlaceholder()}
                                                    multiline
                                                    rows={2}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Autocomplete<string, false, false, true>
                                        freeSolo
                                        options={locationOptions}
                                        value={location}
                                        onChange={(_e, value) => setLocation(value ?? '')}
                                        onInputChange={(_e, value) => setLocation(value ?? '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                label={LL.eventModal.location()}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    },
                                                }}
                                            />
                                        )}
                                    />

                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {LL.eventModal.excludedCategories()}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {eventId &&
                                                    templateExcludedCategories.length > 0 && (
                                                        <Tooltip
                                                            title={LL.eventModal.resetToTemplate()}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={
                                                                    handleResetExcludedCategories
                                                                }
                                                            >
                                                                <ResetIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                {excludedCategories.length > 0 && (
                                                    <Tooltip title={LL.eventModal.clearAll()}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClearExcludedCategories}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                        <FormControl fullWidth>
                                            <Select
                                                multiple
                                                value={excludedCategories}
                                                onChange={(e) =>
                                                    setExcludedCategories(
                                                        e.target.value as number[]
                                                    )
                                                }
                                                displayEmpty
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {selected.map((categoryId) => {
                                                            const category = categories?.find(
                                                                (c) => c.id === categoryId
                                                            );
                                                            if (!category) return null;
                                                            return (
                                                                <Chip
                                                                    key={categoryId}
                                                                    icon={
                                                                        <Box
                                                                            sx={{ display: 'flex' }}
                                                                        >
                                                                            <CategoryIcon
                                                                                category={
                                                                                    category.icon
                                                                                }
                                                                                size="16px"
                                                                            />
                                                                        </Box>
                                                                    }
                                                                    label={category.name}
                                                                    size="small"
                                                                    onDelete={(e) => {
                                                                        e.stopPropagation();
                                                                        setExcludedCategories(
                                                                            excludedCategories.filter(
                                                                                (id) =>
                                                                                    id !==
                                                                                    categoryId
                                                                            )
                                                                        );
                                                                    }}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    sx={{ height: 24 }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    },
                                                }}
                                            >
                                                {categories?.map((category) => (
                                                    <MenuItem key={category.id} value={category.id}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <CategoryIcon
                                                                category={category.icon}
                                                                size="20px"
                                                            />
                                                            <ListItemText primary={category.name} />
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </Box>
                        </Collapse>
                    </Paper>

                    {/* Cancelation stays at the bottom */}
                    {canEdit && (
                        <Paper
                            variant="outlined"
                            sx={(theme) => ({
                                p: 2,
                                borderRadius: 2,
                                borderColor: canceled ? theme.palette.warning.main : 'divider',
                                backgroundColor: canceled
                                    ? theme.palette.warning.light
                                    : theme.palette.action.hover,
                                transition: 'background-color 150ms ease, border-color 150ms ease',
                            })}
                        >
                            <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack
                                        direction="row"
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {LL.eventModal.canceled()}
                                            </Typography>
                                        </Box>

                                        <Switch
                                            checked={canceled}
                                            onChange={(_, checked) => setCanceled(checked)}
                                            color="warning"
                                            slotProps={{
                                                input: {
                                                    'aria-label': LL.eventModal.canceled(),
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </Box>
        </BaseModal>
    );
};

export default EventModal;
